import { inject, Injectable } from '@angular/core';
import { AuthService, DismissedPopupKeywords, LocalStorage } from '@isophi/core-legacy';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InnitialPopupComponent } from '../components/innitial-popup/innitial-popup.component';
import { filter, map, ReplaySubject, take } from 'rxjs';
import { KindergartenService } from '@app/core/services/kindergarten.service';
import { DemoPopupComponent } from '../components/demo-popup/demo-popup.component';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  private readonly modalService = inject(NgbModal);
  private readonly authService = inject(AuthService);
  private readonly kindergartenService = inject(KindergartenService);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly localStorage = new LocalStorage();

  /** Emits when any of the opened modals closes */
  readonly modalClosed$ = new ReplaySubject<void>(void 0);

  private readonly openedPopups: Partial<Record<DismissedPopupKeywords, NgbModalRef>> = {};

  tryShowPopup(popupKeyword: DismissedPopupKeywords) {
    const defaultModalConf: NgbModalOptions = {
      size: 'lg',
    };
    const initialPopupModalConf: NgbModalOptions = {
      ...defaultModalConf,
      centered: true,
      windowClass: 'pap-initial-popup-modal',
    };
    return this.authService.loggedUser$.pipe(
      filter((user) => !!user),
      map((user) => {
        if (user.teacher.dismissedPopupKeywords.some((dismissed) => dismissed === popupKeyword) || !!this.openedPopups[popupKeyword]) {
          return false;
        }

        const demoKey = `${'pap_app_demo' as DismissedPopupKeywords}-${this.activatedRoute.snapshot.queryParams['originUserUuid']}`;

        // Check if demo popup should be shown
        // Show only when going from 'normal' account to demo, not when going from demo to demo. That is handled by query param
        if (
          this.kindergartenService.loggedKindergarten.isDemo &&
          popupKeyword === 'pap_app_demo' &&
          this.activatedRoute.snapshot.queryParams['showDemoPopup'] &&
          !this.localStorage.getItem(demoKey)
        ) {
          this.openedPopups[popupKeyword] = this.modalService.open(DemoPopupComponent, defaultModalConf);
          this.openedPopups[popupKeyword].componentInstance.popupKey = popupKeyword;
          this.openedPopups[popupKeyword].componentInstance.localKey = demoKey;
          return true;
        }

        // all popups (except demo) should not be displayed in demo kindergarten
        if (this.kindergartenService.loggedKindergarten.isDemo) {
          return false;
        }

        switch (popupKeyword) {
          case 'pap_app_initial':
            this.openedPopups[popupKeyword] = this.modalService.open(InnitialPopupComponent, initialPopupModalConf);
            this.openedPopups[popupKeyword].componentInstance.popupKey = popupKeyword;
            return true;
          default:
            return false;
        }
      }),
      take(1)
    );
  }

  /** Dismiss popup from ever showing again */
  dismissPopup(popUpKey: DismissedPopupKeywords) {
    this.kindergartenService.dismissTeacherPopup(popUpKey);
  }

  closePopup(popupKeyword: DismissedPopupKeywords, onDestroy = false) {
    const previouslyOpened = this.openedPopups[popupKeyword];
    if (previouslyOpened) {
      if (!onDestroy) {
        previouslyOpened.close();
      }
      this.modalClosed$.next();
    }
    delete this.openedPopups[popupKeyword];
  }
}
